// ──────────────────────────────────────────
// Cookie Consent
//
// Two-tier consent: 'necessary' (functional only) or 'all'
// (functional + third-party media such as YouTube embeds).
// Choice is stored in a first-party cookie for 182 days.
// ──────────────────────────────────────────

const COOKIE_NAME = 'vdz_cookie_consent';
const COOKIE_DAYS = 182;

const VALID_VALUES = ['all', 'necessary'];

/**
 * Read the stored consent choice.
 * @returns {'all'|'necessary'|null}
 */
export function readConsent() {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)'));
    const value = match ? decodeURIComponent(match[1]) : null;
    return VALID_VALUES.includes(value) ? value : null;
}

function writeConsent(value) {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = COOKIE_NAME + '=' + encodeURIComponent(value)
        + '; max-age=' + (COOKIE_DAYS * 86400)
        + '; path=/; SameSite=Lax' + secure;
}

/** True when the visitor consented to third-party media (YouTube etc.). */
export function hasMediaConsent() {
    return readConsent() === 'all';
}

/**
 * Store a consent choice, notify listeners, and activate gated embeds
 * when media consent was granted.
 * @param {'all'|'necessary'} value
 */
export function grantConsent(value) {
    writeConsent(value);
    window.dispatchEvent(new CustomEvent('vdz:consent', { detail: value }));
    if (value === 'all') {
        activateGatedEmbeds();
    }
}

/**
 * Replace every consent placeholder with its real YouTube iframe.
 * Placeholders are rendered by vdz_youtube_embed() in functions.php.
 */
export function activateGatedEmbeds() {
    document.querySelectorAll('.js-embed-gate[data-yt-id]').forEach((gate) => {
        const iframe = document.createElement('iframe');
        iframe.className = 'w-full h-full';
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(gate.dataset.ytId);
        iframe.title = gate.dataset.ytTitle || 'YouTube video player';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        gate.replaceWith(iframe);
    });
}

/**
 * Alpine component for the banner UI (templates/partials/cookie-banner.php).
 */
export default function cookieBanner() {
    return {
        open: false,

        init() {
            this.open = readConsent() === null;

            // Footer "Cookie settings" link re-opens the banner.
            window.addEventListener('vdz:consent-open', () => { this.open = true; });

            // Any consent decision (banner or embed placeholder) closes it.
            window.addEventListener('vdz:consent', () => { this.open = false; });
        },

        acceptAll() {
            grantConsent('all');
        },

        acceptNecessary() {
            grantConsent('necessary');
        },
    };
}

/**
 * Global wiring, called once from app.js:
 * - activate embeds immediately when consent was given on a previous visit
 * - delegate clicks on placeholder "accept & play" buttons
 * - delegate clicks on "Cookie settings" links
 */
export function initCookieConsent() {
    if (hasMediaConsent()) {
        activateGatedEmbeds();
    }

    document.addEventListener('click', (e) => {
        if (e.target.closest('[data-consent-accept-media]')) {
            grantConsent('all');
        }
        if (e.target.closest('[data-consent-settings]')) {
            window.dispatchEvent(new CustomEvent('vdz:consent-open'));
        }
    });
}
